import React, { useRef, useEffect, useState, useMemo } from 'react';
import * as d3 from 'd3';

const CHART_WIDTH = 700;
const CHART_HEIGHT = 350;
const MARGIN = { top: 20, right: 20, bottom: 50, left: 60 };

const LINES = [
  { key: 'overall', label: 'Overall', color: '#2563eb', width: 2.5 },
  { key: 'pest365', label: 'Pest365', color: '#16a34a', width: 1.5 },
  { key: 'pest365Plus', label: 'Pest365+', color: '#7c3aed', width: 1.5 },
  { key: 'oneTime', label: 'One-Time', color: '#f59e0b', width: 1.5 },
];

const styles = {
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  header: {
    marginBottom: '12px',
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1f2937',
    marginBottom: '2px',
  },
  subtitle: {
    fontSize: '12px',
    color: '#9ca3af',
    marginBottom: '0',
  },
  legend: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '16px',
    marginBottom: '12px',
    fontSize: '12px',
    color: '#6b7280',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  toggleGroup: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px',
    marginBottom: '12px',
  },
  toggleButton: {
    padding: '4px 10px',
    fontSize: '11px',
    fontWeight: 500,
    border: '1px solid #e5e7eb',
    borderRadius: '4px',
    cursor: 'pointer',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    transition: 'background 0.15s, color 0.15s',
  },
  container: {
    overflowX: 'auto',
  },
  tooltip: {
    position: 'fixed',
    pointerEvents: 'none',
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    padding: '8px 12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
    fontSize: '12px',
    zIndex: 1000,
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
};

function formatDateLabel(dateStr) {
  const date = new Date(dateStr);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[date.getMonth()]} ${date.getDate()}`;
}

function formatDateFull(dateStr) {
  const date = new Date(dateStr);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
}

export default function RollingVolatilityChart({ data }) {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);
  const [hiddenLines, setHiddenLines] = useState(new Set());

  const visibleLines = useMemo(() => {
    return LINES.filter((l) => !hiddenLines.has(l.key));
  }, [hiddenLines]);

  const toggleLine = (key) => {
    setHiddenLines((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  useEffect(() => {
    if (!data || !data.dates || data.dates.length === 0) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const containerWidth = containerRef.current
      ? containerRef.current.clientWidth
      : CHART_WIDTH;
    const width = Math.max(600, containerWidth);
    const height = CHART_HEIGHT;
    const innerW = width - MARGIN.left - MARGIN.right;
    const innerH = height - MARGIN.top - MARGIN.bottom;

    svg
      .attr('viewBox', `0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`)
      .attr('width', '100%')
      .attr('preserveAspectRatio', 'xMidYMid meet');

    const g = svg.append('g').attr('transform', `translate(${MARGIN.left}, ${MARGIN.top})`);

    const vbInnerW = CHART_WIDTH - MARGIN.left - MARGIN.right;
    const vbInnerH = CHART_HEIGHT - MARGIN.top - MARGIN.bottom;

    const dates = data.dates;
    const n = dates.length;
    const threshold = data.threshold != null ? data.threshold : null;

    // Collect all values for Y domain
    const allValues = [];
    if (data.overall) allValues.push(...data.overall.filter((v) => v != null));
    if (data.byField) {
      if (data.byField.pest365) allValues.push(...data.byField.pest365.filter((v) => v != null));
      if (data.byField.pest365Plus) allValues.push(...data.byField.pest365Plus.filter((v) => v != null));
      if (data.byField.oneTime) allValues.push(...data.byField.oneTime.filter((v) => v != null));
    }
    if (threshold != null) allValues.push(threshold);

    const yMax = d3.max(allValues) || 1;
    const yPadding = yMax * 0.1 || 0.5;

    // Scales
    const x = d3.scalePoint()
      .domain(dates)
      .range([0, vbInnerW]);

    const y = d3.scaleLinear()
      .domain([0, yMax + yPadding])
      .nice()
      .range([vbInnerH, 0]);

    // Y-axis gridlines
    const yTicks = y.ticks(6);
    g.selectAll('.grid-line')
      .data(yTicks)
      .enter()
      .append('line')
      .attr('x1', 0)
      .attr('x2', vbInnerW)
      .attr('y1', (d) => y(d))
      .attr('y2', (d) => y(d))
      .attr('stroke', '#f3f4f6')
      .attr('stroke-width', 1);

    // Area fill below overall line (light blue)
    if (data.overall && !hiddenLines.has('overall')) {
      const overallArea = d3.area()
        .x((d, i) => x(dates[i]))
        .y0(vbInnerH)
        .y1((d) => y(d))
        .defined((d) => d != null)
        .curve(d3.curveMonotoneX);

      g.append('path')
        .datum(data.overall)
        .attr('d', overallArea)
        .attr('fill', '#2563eb')
        .attr('fill-opacity', 0.08)
        .attr('stroke', 'none');

      // Red shading where overall exceeds threshold
      if (threshold != null) {
        const exceedArea = d3.area()
          .x((d, i) => x(dates[i]))
          .y0(() => y(threshold))
          .y1((d) => y(Math.max(d, threshold)))
          .defined((d) => d != null)
          .curve(d3.curveMonotoneX);

        g.append('path')
          .datum(data.overall)
          .attr('d', exceedArea)
          .attr('fill', '#dc2626')
          .attr('fill-opacity', 0.12)
          .attr('stroke', 'none');
      }
    }

    // Threshold reference line
    if (threshold != null) {
      g.append('line')
        .attr('x1', 0)
        .attr('x2', vbInnerW)
        .attr('y1', y(threshold))
        .attr('y2', y(threshold))
        .attr('stroke', '#dc2626')
        .attr('stroke-width', 1)
        .attr('stroke-dasharray', '6,4');

      g.append('text')
        .attr('x', vbInnerW)
        .attr('y', y(threshold) - 6)
        .attr('text-anchor', 'end')
        .attr('font-size', '9px')
        .attr('fill', '#dc2626')
        .attr('font-weight', 500)
        .text('High Volatility Threshold');
    }

    // Helper to get series values
    const getSeriesValues = (key) => {
      if (key === 'overall') return data.overall;
      if (data.byField && data.byField[key]) return data.byField[key];
      return null;
    };

    // Draw lines for each visible series
    const lineGen = d3.line()
      .x((d, i) => x(dates[i]))
      .y((d) => y(d))
      .defined((d) => d != null)
      .curve(d3.curveMonotoneX);

    LINES.forEach((lineDef) => {
      if (hiddenLines.has(lineDef.key)) return;
      const values = getSeriesValues(lineDef.key);
      if (!values) return;

      g.append('path')
        .datum(values)
        .attr('d', lineGen)
        .attr('fill', 'none')
        .attr('stroke', lineDef.color)
        .attr('stroke-width', lineDef.width);
    });

    // X axis
    g.append('g')
      .attr('transform', `translate(0, ${vbInnerH})`)
      .call(d3.axisBottom(x).tickSize(0).tickFormat((d) => formatDateLabel(d)))
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280')
      .attr('transform', 'rotate(-30)')
      .attr('text-anchor', 'end')
      .attr('dx', '-0.5em')
      .attr('dy', '0.25em');

    g.select('.domain').attr('stroke', '#e5e7eb');

    // Y axis
    g.append('g')
      .call(
        d3.axisLeft(y)
          .ticks(6)
          .tickSize(0)
          .tickFormat((v) => `$${v.toFixed(2)}`)
      )
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280');

    g.selectAll('.domain').attr('stroke', '#e5e7eb');
    g.selectAll('.tick line').attr('stroke', '#e5e7eb');

    // Vertical crosshair (initially hidden)
    const crosshair = g.append('line')
      .attr('y1', 0)
      .attr('y2', vbInnerH)
      .attr('stroke', '#d1d5db')
      .attr('stroke-width', 1)
      .attr('stroke-dasharray', '3,3')
      .style('display', 'none');

    // Invisible overlay columns for tooltip hover
    const colWidth = n > 1 ? vbInnerW / (n - 1) : vbInnerW;

    dates.forEach((date, i) => {
      const cx = x(date);
      const halfW = colWidth / 2;

      g.append('rect')
        .attr('x', cx - halfW)
        .attr('y', 0)
        .attr('width', colWidth)
        .attr('height', vbInnerH)
        .attr('fill', 'transparent')
        .style('cursor', 'crosshair')
        .on('mousemove', (event) => {
          crosshair
            .attr('x1', cx)
            .attr('x2', cx)
            .style('display', null);

          setTooltip({
            x: event.clientX + 12,
            y: event.clientY - 10,
            date,
            overall: data.overall ? data.overall[i] : null,
            pest365: data.byField && data.byField.pest365 ? data.byField.pest365[i] : null,
            pest365Plus: data.byField && data.byField.pest365Plus ? data.byField.pest365Plus[i] : null,
            oneTime: data.byField && data.byField.oneTime ? data.byField.oneTime[i] : null,
          });
        })
        .on('mouseleave', () => {
          crosshair.style('display', 'none');
          setTooltip(null);
        });
    });

    return () => {
      svg.selectAll('*').remove();
    };
  }, [data, hiddenLines]);

  // Empty state
  if (!data || !data.dates || data.dates.length === 0) {
    return (
      <div style={styles.card}>
        <div style={styles.header}>
          <div style={styles.title}>Rolling Price Volatility</div>
          <div style={styles.subtitle}>Standard deviation of prices over a rolling window</div>
        </div>
        <div style={{ color: '#6b7280', textAlign: 'center', padding: '40px 0' }}>
          No data available
        </div>
      </div>
    );
  }

  const tooltipRows = tooltip
    ? LINES.map((l) => ({
        label: l.label,
        color: l.color,
        value: tooltip[l.key],
        bold: l.key === 'overall',
      }))
    : [];

  return (
    <div style={styles.card}>
      <div style={styles.header}>
        <div style={styles.title}>Rolling Price Volatility</div>
        <div style={styles.subtitle}>Standard deviation of prices over a rolling window</div>
      </div>

      {/* Toggle buttons */}
      <div style={styles.toggleGroup}>
        {LINES.map((l) => {
          const isActive = !hiddenLines.has(l.key);
          return (
            <button
              key={l.key}
              style={{
                ...styles.toggleButton,
                background: isActive ? l.color : '#fff',
                color: isActive ? '#fff' : '#6b7280',
                borderColor: isActive ? l.color : '#e5e7eb',
              }}
              onClick={() => toggleLine(l.key)}
            >
              {l.label}
            </button>
          );
        })}
      </div>

      {/* Legend */}
      <div style={styles.legend}>
        {LINES.map((l) => (
          <div key={l.key} style={styles.legendItem}>
            <svg width="20" height="10">
              <line
                x1="0"
                y1="5"
                x2="20"
                y2="5"
                stroke={l.color}
                strokeWidth={l.width}
              />
            </svg>
            <span style={{ color: hiddenLines.has(l.key) ? '#d1d5db' : '#6b7280' }}>
              {l.label}
            </span>
          </div>
        ))}
        {data.threshold != null && (
          <div style={styles.legendItem}>
            <svg width="20" height="10">
              <line
                x1="0"
                y1="5"
                x2="20"
                y2="5"
                stroke="#dc2626"
                strokeWidth="1"
                strokeDasharray="6,4"
              />
            </svg>
            <span>Threshold</span>
          </div>
        )}
      </div>

      <div style={styles.container} ref={containerRef}>
        <svg ref={svgRef} />
      </div>

      {/* Tooltip */}
      {tooltip && (
        <div
          style={{
            ...styles.tooltip,
            left: tooltip.x,
            top: tooltip.y,
          }}
        >
          <div style={{ fontWeight: 600, color: '#1f2937', marginBottom: '4px' }}>
            {formatDateFull(tooltip.date)}
          </div>
          {tooltipRows.map((row) => (
            <div
              key={row.label}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: '16px',
                marginBottom: '2px',
              }}
            >
              <span style={{ color: row.color, fontWeight: row.bold ? 600 : 400 }}>
                {row.label}
              </span>
              <span style={{ fontWeight: 600 }}>
                {row.value != null ? `$${row.value.toFixed(2)}` : '--'}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
